import { type LabChemical } from "@/data/reactions";
import { type Reaction } from "@/data/reactions";
import { X, Plus, Minus } from "@/lib/icons";
import { ArrowLeft, NotebookPen } from "@/lib/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useStopAnimations } from "@/hooks/useStopAnimations";
import { useEffect, useRef, useState } from "react";

interface MixingBeakerProps {
  chemicals: LabChemical[];
  onRemoveChemical: (index: number) => void;
  reaction: Reaction | null;
  isReacting: boolean;
  onDrop: (chemical: LabChemical) => void;
  /** 0.1 – 1.0; affects liquid opacity and bubbling speed. */
  concentration?: number;
  /** Temperature in °C - drives steam (≥80) and ice (≤5) visuals. */
  temperature?: number;
}

// Deterministic pseudo-random so re-renders don't reshuffle bubbles mid-animation
function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

interface Bubble {
  id: number;
  cx: number;
  r: number;
  drift: number;
  delay: number;
  duration: number;
  /** "white" for foam, "tint" for liquid-colored, "highlight" for brighter */
  variant: "white" | "tint" | "highlight";
  opacity: number;
  /** Larger surface-popping bubbles get extra squash on impact */
  burst?: boolean;
}

// Reaction-type-specific bubble character.
// thresholds are cumulative for a 0–1 random:
//   r < whiteMax           → white foam
//   r < whiteMax+tintMax   → liquid-tinted
//   else                   → bright highlight
const TYPE_VARIANT_MIX: Record<string, { whiteMax: number; tintMax: number }> = {
  // Acid+metal / single-replacement → vigorous H₂ foam
  "single-replacement": { whiteMax: 0.78, tintMax: 0.17 },
  "acid-base":          { whiteMax: 0.65, tintMax: 0.25 },
  // Combustion → mostly hot bright bubbles
  "combustion":         { whiteMax: 0.25, tintMax: 0.30 },
  // Decomposition (often heated) → balanced
  "decomposition":      { whiteMax: 0.45, tintMax: 0.35 },
  // Precipitation / synthesis → more colored, fewer foamy bubbles
  "double-replacement": { whiteMax: 0.30, tintMax: 0.50 },
  "synthesis":          { whiteMax: 0.40, tintMax: 0.40 },
};
const DEFAULT_VARIANT_MIX = { whiteMax: 0.55, tintMax: 0.30 };

// ---------- Color parsing / blending ----------
// Parse a CSS color into HSL components. Supports `hsl(h, s%, l%)`,
// `hsl(h s% l%)`, `#rgb`, `#rrggbb`, and falls back to a neutral gray.
interface HSL {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function parseColor(input: string): HSL {
  if (!input) return { h: 200, s: 30, l: 90 };
  const s = input.trim().toLowerCase();

  // hsl(h, s%, l%) or hsl(h s% l%)
  const hslMatch = s.match(/hsl\(\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)%\s*[, ]\s*(\d+(?:\.\d+)?)%/);
  if (hslMatch) {
    return { h: parseFloat(hslMatch[1]), s: parseFloat(hslMatch[2]), l: parseFloat(hslMatch[3]) };
  }

  // #rgb / #rrggbb
  const hexMatch = s.match(/^#([0-9a-f]{3,8})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return rgbToHsl(r, g, b);
    }
  }

  // rgb(r, g, b)
  const rgbMatch = s.match(/rgb\(\s*(\d+)[ ,]+(\d+)[ ,]+(\d+)/);
  if (rgbMatch) {
    return rgbToHsl(parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3]));
  }

  return { h: 200, s: 30, l: 90 };
}

function hslToString({ h, s, l }: HSL): string {
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

// Average HSL across colors using vector-mean on the hue circle so that
// e.g. red + blue blends through purple rather than green.
function blendColors(colors: string[]): HSL {
  if (colors.length === 0) return { h: 200, s: 30, l: 90 };
  let sumX = 0;
  let sumY = 0;
  let sumS = 0;
  let sumL = 0;
  let weight = 0;
  for (const c of colors) {
    const hsl = parseColor(c);
    // Weight hue contribution by saturation so near-grays don't pull the hue
    const w = Math.max(0.1, hsl.s / 100);
    const rad = (hsl.h * Math.PI) / 180;
    sumX += Math.cos(rad) * w;
    sumY += Math.sin(rad) * w;
    sumS += hsl.s;
    sumL += hsl.l;
    weight += w;
  }
  const h = ((Math.atan2(sumY / weight, sumX / weight) * 180) / Math.PI + 360) % 360;
  return {
    h,
    s: sumS / colors.length,
    l: sumL / colors.length,
  };
}

function shiftLightness({ h, s, l }: HSL, delta: number): HSL {
  return { h, s, l: Math.max(0, Math.min(100, l + delta)) };
}

/** Boost saturation (positive delta) or fade toward gray (negative). */
function shiftSaturation({ h, s, l }: HSL, delta: number): HSL {
  return { h, s: Math.max(0, Math.min(100, s + delta)), l };
}

export default function MixingBeaker({
  chemicals,
  onRemoveChemical,
  reaction,
  isReacting,
  onDrop,
  concentration = 0.5,
  temperature = 25,
}: MixingBeakerProps) {
  const reduceMotion = useStopAnimations();
  const liquidOpacity = 0.3 + concentration * 0.6; // 0.36 – 0.9
  const bubbleDuration = 2.5 - concentration * 1.5; // dilute → slow, concentrated → fast
  const isHot = temperature >= 80;
  const isCold = temperature <= 5;

  // Per-chip quantity (visual only - affects fill level & bubble vigor).
  // Resets when the beaker count changes structurally.
  const [quantities, setQuantities] = useState<number[]>([]);
  useEffect(() => {
    setQuantities((prev) => {
      if (prev.length === chemicals.length) return prev;
      // Preserve overlap, default new entries to 1.
      return chemicals.map((_, i) => prev[i] ?? 1);
    });
  }, [chemicals.length]);
  const totalQuantity = quantities.reduce((a, b) => a + b, 0) || chemicals.length;

  const adjustQuantity = (i: number, delta: number) => {
    setQuantities((prev) => {
      const next = [...prev];
      const cur = next[i] ?? 1;
      next[i] = Math.max(1, Math.min(5, cur + delta));
      return next;
    });
  };

  // Track previous chemical count to detect "add" events for the pour-in droplet
  const prevCountRef = useRef(chemicals.length);
  const pourSeqRef = useRef(0);
  const [pour, setPour] = useState<{ key: number; color: string } | null>(null);

  useEffect(() => {
    if (chemicals.length > prevCountRef.current) {
      const added = chemicals[chemicals.length - 1];
      pourSeqRef.current += 1;
      setPour({ key: pourSeqRef.current, color: added.color });
    }
    prevCountRef.current = chemicals.length;
  }, [chemicals]);

  // Glow-ring pulse: fire a one-shot when a reaction begins
  const reactionIdRef = useRef<string | null>(null);
  const [glowKey, setGlowKey] = useState(0);
  const [glowColor, setGlowColor] = useState<string | null>(null);

  useEffect(() => {
    const currentId = reaction && isReacting ? reaction.id ?? reaction.equation : null;
    if (currentId && currentId !== reactionIdRef.current) {
      setGlowColor(reaction!.color || chemicals[chemicals.length - 1]?.color || "hsl(var(--primary))");
      setGlowKey((k) => k + 1);
    }
    reactionIdRef.current = currentId;
  }, [reaction, isReacting, chemicals]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData("chemical");
      if (data) onDrop(JSON.parse(data));
    } catch {}
  };

  // Smoothly blend colors of all chemicals in the beaker. When a reaction
  // is active, prefer the reaction's product color but still mix slightly
  // with the chemicals so quick adds feel continuous.
  const blendedHsl = blendColors(chemicals.map((c) => c.color));
  const baseLiquidHsl: HSL = reaction?.color
    ? blendColors([reaction.color, reaction.color, hslToString(blendedHsl)]) // 2:1 toward reaction color
    : blendedHsl;
  // Concentration drives saturation: dilute (10%) fades toward gray, concentrated (100%) deepens color.
  // Map concentration 0.1–1.0 → saturation delta −18 … +18.
  const satDelta = (concentration - 0.55) * 40;
  const liquidHsl: HSL = shiftSaturation(baseLiquidHsl, satDelta);
  const liquidColor = chemicals.length > 0 ? hslToString(liquidHsl) : "hsl(200, 30%, 90%)";
  const tintBubbleColor = hslToString(shiftLightness(liquidHsl, 25));
  const highlightBubbleColor = hslToString(shiftLightness(liquidHsl, 40));

  // Level scales with both number of chemicals AND total quantity.
  const liquidLevel = Math.min(80, 18 + totalQuantity * 9);
  const liquidTopY = 240 - (liquidLevel / 100) * 220;
  const liquidPath = `M 41.5 ${liquidTopY} L 158.5 ${liquidTopY} L 158.5 200 Q 158.5 238.5 130 238.5 L 70 238.5 Q 41.5 238.5 41.5 200 Z`;

  // Generate randomized bubbles seeded on reaction id so they stay stable
  // for the lifetime of a given reaction but change between reactions.
  const bubbleSeed = reaction ? (reaction.id ?? reaction.equation).length + chemicals.length : 0;
  const variantMix = reaction ? TYPE_VARIANT_MIX[reaction.type] ?? DEFAULT_VARIANT_MIX : DEFAULT_VARIANT_MIX;
  const bubbles: Bubble[] = isReacting && !reduceMotion
    ? Array.from({ length: 14 }, (_, i) => {
        const a = seededRandom(bubbleSeed + i * 7.13);
        const b = seededRandom(bubbleSeed + i * 13.37);
        const c = seededRandom(bubbleSeed + i * 19.71);
        const d = seededRandom(bubbleSeed + i * 23.5);
        const e = seededRandom(bubbleSeed + i * 29.9);
        const f = seededRandom(bubbleSeed + i * 31.1);
        // Mix of foam (white), tinted, and highlight bubbles - ratio tuned per reaction type
        const variant: Bubble["variant"] =
          e < variantMix.whiteMax
            ? "white"
            : e < variantMix.whiteMax + variantMix.tintMax
            ? "tint"
            : "highlight";
        // ~2 of 14 bubbles become large surface-popping "burst" bubbles
        const burst = f > 0.86;
        const baseR = 1.2 + b * 4.3; // 1.2–5.5 px
        const r = burst ? 7 + b * 2 : baseR; // 7–9 px when bursting
        return {
          id: i,
          cx: 55 + a * 90, // spread across beaker interior (42–158)
          r,
          drift: (c - 0.5) * 18, // ±9 px horizontal sway
          delay: d * (bubbleDuration + 0.3) + (burst ? 0.4 : 0),
          duration: bubbleDuration * (burst ? 1.1 : 0.75 + b * 0.6),
          variant,
          opacity: variant === "white" ? 0.65 : variant === "highlight" ? 0.85 : 0.5,
          burst,
        };
      })
    : [];

  return (
    <div className="flex flex-col items-center">
      {/* Beaker */}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="relative w-56 h-72 flex flex-col items-center justify-end"
      >
        {/* One-shot reaction glow ring */}
        <AnimatePresence>
          {glowColor && !reduceMotion && (
            <motion.div
              key={glowKey}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{
                background: `radial-gradient(circle at 50% 60%, ${glowColor} 0%, transparent 65%)`,
                filter: "blur(18px)",
              }}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: [0, 0.85, 0.5, 0], scale: [0.7, 1.15, 1.25, 1.4] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], times: [0, 0.25, 0.6, 1] }}
              onAnimationComplete={() => setGlowColor(null)}
            />
          )}
        </AnimatePresence>

        {/* Beaker shape - SVG */}
        <svg viewBox="0 0 200 260" className="w-full h-full absolute inset-0" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/*
              Inner beaker shape - sits just inside the 2.5px stroke of the
              outline at d="M 40 20 ... Q 40 240 70 240 L 130 240 Q 160 240 160 200 L 160 20".
              We inset by ~1.5px so the liquid hugs the inside wall cleanly.
            */}
            <clipPath id="beakerClip">
              <path d="M 41.5 20 L 41.5 200 Q 41.5 238.5 70 238.5 L 130 238.5 Q 158.5 238.5 158.5 200 L 158.5 20 Z" />
            </clipPath>
          </defs>

          {/* Pour-in droplet (above clip so it falls outside the beaker too) */}
          <AnimatePresence>
            {pour && !reduceMotion && (
              <motion.g key={pour.key} aria-hidden="true">
                <motion.ellipse
                  cx="100"
                  rx="4"
                  ry="6"
                  fill={pour.color}
                  initial={{ cy: -10, opacity: 0, scaleY: 0.7 }}
                  animate={{
                    cy: [-10, liquidTopY - 4],
                    opacity: [0, 1, 1],
                    scaleY: [0.7, 1.1, 1.4],
                  }}
                  transition={{ duration: 0.45, ease: [0.55, 0, 0.85, 0.2] }}
                />
                {/* Splash ring on impact */}
                <motion.ellipse
                  cx="100"
                  cy={liquidTopY}
                  fill="none"
                  stroke={pour.color}
                  strokeWidth="1.5"
                  initial={{ rx: 2, ry: 0.8, opacity: 0 }}
                  animate={{ rx: [2, 18, 26], ry: [0.8, 4, 1], opacity: [0, 0.9, 0] }}
                  transition={{ duration: 0.55, delay: 0.4, ease: "easeOut" }}
                  onAnimationComplete={() => setPour(null)}
                />
                {/* Splash droplets */}
                {[-1, 1].map((dir) => (
                  <motion.circle
                    key={dir}
                    r={1.6}
                    fill={pour.color}
                    initial={{ cx: 100, cy: liquidTopY, opacity: 0 }}
                    animate={{
                      cx: 100 + dir * 14,
                      cy: [liquidTopY, liquidTopY - 8, liquidTopY + 4],
                      opacity: [0, 1, 0],
                    }}
                    transition={{ duration: 0.45, delay: 0.42, ease: "easeOut" }}
                  />
                ))}
              </motion.g>
            )}
          </AnimatePresence>

          {/* Beaker outline */}
          <path
            d="M 40 20 L 40 200 Q 40 240 70 240 L 130 240 Q 160 240 160 200 L 160 20"
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Spout lip */}
          <path d="M 35 20 L 40 20" fill="none" stroke="hsl(var(--border))" strokeWidth="2.5" />
          <path d="M 160 20 L 165 20" fill="none" stroke="hsl(var(--border))" strokeWidth="2.5" />
          {/* Graduation marks */}
          {[60, 100, 140, 180].map((y) => (
            <line key={y} x1="45" y1={y} x2="55" y2={y} stroke="hsl(var(--muted-foreground))" strokeWidth="1" opacity="0.3" />
          ))}

          {/* Liquid fill */}
          {chemicals.length > 0 && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <motion.path
                d={liquidPath}
                animate={{ fill: liquidColor, d: liquidPath }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        fill: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                        d: { type: "spring", stiffness: 60, damping: 15 },
                      }
                }
                opacity={liquidOpacity}
                initial={reduceMotion ? false : { d: `M 41.5 238.5 L 158.5 238.5 L 158.5 200 Q 158.5 238.5 130 238.5 L 70 238.5 Q 41.5 238.5 41.5 200 Z` }}
              />

              {/* Randomized bubbling animation when reacting */}
              {bubbles.map((bub) => (
                <motion.circle
                  key={bub.id}
                  fill={
                    bub.variant === "white"
                      ? "white"
                      : bub.variant === "tint"
                      ? tintBubbleColor
                      : highlightBubbleColor
                  }
                  clipPath="url(#beakerClip)"
                  initial={{ cx: bub.cx, cy: 235, r: bub.r * 0.85, opacity: 0 }}
                  animate={{
                    cx: [bub.cx, bub.cx + bub.drift / 2, bub.cx + bub.drift],
                    cy: [235, liquidTopY + 14, liquidTopY + 2],
                    // Squash-and-stretch: small rise, expand mid-rise, pop/stretch at surface
                    r: bub.burst
                      ? [bub.r * 0.6, bub.r * 1.05, bub.r * 1.4, 0]
                      : [bub.r * 0.85, bub.r * 1.1, bub.r * 0.95, bub.r * 0.7],
                    opacity: [0, bub.opacity, bub.opacity * 0.9, 0],
                  }}
                  transition={{
                    duration: bub.duration,
                    repeat: Infinity,
                    delay: bub.delay,
                    ease: "easeOut",
                    times: [0, 0.45, 0.85, 1],
                  }}
                />
              ))}

              {/* Reduced-motion: show a static "reacting" badge instead of bubbles */}
              {isReacting && reduceMotion && (
                <text
                  x="100"
                  y="130"
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="hsl(var(--primary))"
                >
                  ⚗ reacting
                </text>
              )}
            </motion.g>
          )}

          {/* Ice crystals - appear at the liquid surface when very cold */}
          {chemicals.length > 0 && isCold && !reduceMotion && (
            <g clipPath="url(#beakerClip)" aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => {
                const a = seededRandom(i * 4.7 + 1);
                const b = seededRandom(i * 9.1 + 3);
                const cx = 55 + a * 90;
                const cy = liquidTopY + 2 + b * 4;
                const size = 2.2 + b * 1.6;
                return (
                  <motion.g
                    key={`ice-${i}`}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: [0, 0.85, 0.7], scale: [0.6, 1, 1] }}
                    transition={{ duration: 1.2, delay: i * 0.15, ease: "easeOut" }}
                    style={{ transformOrigin: `${cx}px ${cy}px` }}
                  >
                    {/* Simple snowflake: three crossing lines */}
                    <line x1={cx - size} y1={cy} x2={cx + size} y2={cy}
                      stroke="hsl(200, 80%, 92%)" strokeWidth="0.8" strokeLinecap="round" />
                    <line x1={cx - size * 0.5} y1={cy - size * 0.87} x2={cx + size * 0.5} y2={cy + size * 0.87}
                      stroke="hsl(200, 80%, 92%)" strokeWidth="0.8" strokeLinecap="round" />
                    <line x1={cx + size * 0.5} y1={cy - size * 0.87} x2={cx - size * 0.5} y2={cy + size * 0.87}
                      stroke="hsl(200, 80%, 92%)" strokeWidth="0.8" strokeLinecap="round" />
                  </motion.g>
                );
              })}
            </g>
          )}

          {/* Steam wisps - rise out of the beaker mouth when hot */}
          {chemicals.length > 0 && isHot && !reduceMotion && (
            <g aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => {
                const a = seededRandom(i * 11.3 + 5);
                const b = seededRandom(i * 17.7 + 9);
                const startX = 75 + a * 50;
                const drift = (b - 0.5) * 30;
                // Steam intensity scales with temperature beyond 80 °C, capped at 200.
                const intensity = Math.min(1, (temperature - 80) / 120);
                const opacity = 0.25 + intensity * 0.45;
                return (
                  <motion.ellipse
                    key={`steam-${i}`}
                    rx={6}
                    ry={4}
                    fill="white"
                    initial={{ cx: startX, cy: liquidTopY - 2, opacity: 0, scale: 0.5 }}
                    animate={{
                      cx: [startX, startX + drift / 2, startX + drift],
                      cy: [liquidTopY - 2, liquidTopY - 40, liquidTopY - 90],
                      opacity: [0, opacity, 0],
                      scale: [0.5, 1.4, 2.2],
                    }}
                    transition={{
                      duration: 2.4,
                      delay: i * 0.4,
                      repeat: Infinity,
                      ease: "easeOut",
                    }}
                  />
                );
              })}
            </g>
          )}
        </svg>

        {/* Empty state - friendly onboarding hint, constrained to beaker interior */}
        {chemicals.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-1.5 w-[58%] text-center">
              <motion.div
                initial={{ x: 0, opacity: 0.7 }}
                animate={reduceMotion ? {} : { x: [-4, 3, -4], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="flex items-center gap-1 text-primary"
                aria-hidden="true"
              >
                <ArrowLeft size={14} />
                <span className="text-[10px] font-medium">Pick a chemical</span>
              </motion.div>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Drag or click to add
              </p>
              <div className="flex items-center gap-1 text-[9px] text-muted-foreground/80">
                <NotebookPen size={9} aria-hidden="true" />
                <span>Try <span className="font-mono text-foreground">HCl + NaOH</span></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chemical chips in beaker, with per-chip quantity (+/-) */}
      <div className="flex flex-wrap gap-1.5 mt-2 justify-center max-w-[280px]">
        <AnimatePresence>
          {chemicals.map((chem, i) => {
            const qty = quantities[i] ?? 1;
            return (
              <motion.div
                key={`${chem.id}-${i}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border border-border bg-card text-xs"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: chem.color }}
                  aria-hidden="true"
                />
                <span className="font-mono text-foreground">{chem.formula}</span>
                <div className="flex items-center gap-0.5 ml-1 border-l border-border/60 pl-1" role="group" aria-label={`${chem.name} quantity`}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); adjustQuantity(i, -1); }}
                    disabled={qty <= 1}
                    aria-label={`Decrease ${chem.name} amount`}
                    className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus size={10} />
                  </button>
                  <span className="font-mono tabular-nums text-[10px] text-foreground min-w-[12px] text-center" aria-live="polite">
                    {qty}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); adjustQuantity(i, 1); }}
                    disabled={qty >= 5}
                    aria-label={`Increase ${chem.name} amount`}
                    className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={10} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemoveChemical(i); }}
                  aria-label={`Remove ${chem.name}`}
                  className="ml-0.5 p-0.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X size={12} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
